import { useInfiniteQuery } from '@tanstack/react-query';
import { ArrowLeft, Box, ExternalLink } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { getDeliveryView, type TrackCard } from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { Button } from '../../design-system/button.js';
import { StatusPill } from '../../design-system/status-pill.js';
import { Banner, Panel, StateSurface } from '../../design-system/surfaces.js';
import { usePlaybackTransport } from '../../player/index.js';
import {
  TrackCardSurface,
  TrackRow,
  playbackItemFromTrack,
  useTrackPlaybackAction,
} from '../tracks/track-presentation.js';

function DeliveredTrack({
  delivery,
  onStart,
  track,
}: {
  delivery: { status: string };
  onStart: () => Promise<void> | void;
  track: TrackCard;
}) {
  const playback = useTrackPlaybackAction(track, onStart);
  const trailing = <StatusPill status={delivery.status}>{delivery.status}</StatusPill>;
  return (
    <>
      <TrackRow action={playback} track={track} trailing={trailing} />
      <TrackCardSurface action={playback} track={track} trailing={trailing} />
    </>
  );
}

export function DeliveryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { library } = useWorkspace();
  const player = usePlaybackTransport();
  const detail = useInfiniteQuery({
    queryKey: ['delivery', id, library.id],
    initialPageParam: undefined as string | undefined,
    enabled: Boolean(id),
    queryFn: ({ pageParam }) =>
      getDeliveryView(id!, {
        library_id: library.id,
        limit: 50,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    getNextPageParam: (page) => page.deliveries.next_cursor ?? undefined,
    refetchInterval: ({ state }) => {
      const request = state.data?.pages[0]?.request;
      return request && ['pending', 'expanding', 'in_progress'].includes(request.status)
        ? 5_000
        : false;
    },
  });

  if (!id) return <StateSurface kind="error" title="Delivery identity is missing" />;
  if (detail.isPending) return <StateSurface kind="loading" title="Loading delivery" />;
  if (detail.isError) {
    return (
      <StateSurface
        action={
          <Button onClick={() => void detail.refetch()} size="small" variant="secondary">
            Try again
          </Button>
        }
        kind="error"
        title="Delivery could not be loaded"
      />
    );
  }

  const request = detail.data.pages[0]!.request;
  const deliveries = detail.data.pages.flatMap((page) => page.deliveries.items);
  const playbackItems = deliveries.flatMap(({ track }) => playbackItemFromTrack(track) ?? []);
  const playbackContext = {
    kind: 'delivery' as const,
    id: `delivery:${request.id}`,
    label: 'Delivery tracks',
  };
  const startDeliveryPlayback = async (trackId: string) => {
    const startIndex = playbackItems.findIndex((item) => item.id === trackId);
    if (startIndex < 0) return;
    await player.playQueue(playbackItems, startIndex, playbackContext);
    const completeDeliveries = [...deliveries];
    const seenCursors = new Set<string>();
    let cursor = detail.data?.pages.at(-1)?.deliveries.next_cursor ?? null;
    try {
      while (cursor && !seenCursors.has(cursor)) {
        seenCursors.add(cursor);
        const page = await getDeliveryView(request.id, {
          library_id: library.id,
          limit: 50,
          cursor,
        });
        completeDeliveries.push(...page.deliveries.items);
        cursor = page.deliveries.next_cursor;
      }
    } catch {
      return;
    }
    const completeItems = completeDeliveries.flatMap(
      ({ track }) => playbackItemFromTrack(track) ?? [],
    );
    player.appendQueue(playbackContext.id, completeItems);
  };
  return (
    <article>
      <Link
        className="inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-ink-2 outline-none hover:bg-surface hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
        to="/activity"
      >
        <ArrowLeft aria-hidden="true" className="size-4" /> Activity
      </Link>
      <header className="mt-4 flex flex-col gap-5 border-b border-border pb-7 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.16em] text-accent">
            External delivery
          </p>
          <h1 className="mt-3 text-4xl font-semibold tracking-[-0.045em] text-ink sm:text-5xl">
            Delivery request
          </h1>
          <p className="mt-3 break-all font-mono text-xs text-ink-3">{request.id}</p>
        </div>
        <StatusPill status={request.status}>{request.status}</StatusPill>
      </header>

      <div className="mt-6 grid gap-5 lg:grid-cols-[minmax(0,1.5fr)_minmax(18rem,0.5fr)]">
        <Panel>
          <div className="flex items-center gap-3">
            <div className="grid size-11 place-items-center rounded-md border border-accent-line bg-accent-soft text-accent">
              <ExternalLink aria-hidden="true" className="size-5" />
            </div>
            <div>
              <h2 className="font-semibold text-ink">Request progress</h2>
              <p className="mt-1 font-mono text-xs text-ink-3">
                archive-wav · requested {new Date(request.requested_at).toLocaleString()}
              </p>
            </div>
          </div>
          <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {(
              [
                ['Total', request.delivery_counts.total],
                ['Pending', request.delivery_counts.pending],
                ['Succeeded', request.delivery_counts.succeeded],
                ['Failed', request.delivery_counts.failed],
              ] as const
            ).map(([label, value]) => (
              <div className="rounded-md border border-border bg-bg-2 p-3" key={label}>
                <dt className="font-mono text-xs uppercase tracking-[0.1em] text-ink-3">{label}</dt>
                <dd className="mt-2 text-2xl font-semibold text-ink">{value}</dd>
              </div>
            ))}
          </dl>
          {request.failure ? (
            <Banner className="mt-5" title="Delivery failed" tone="danger">
              {request.failure.message}
            </Banner>
          ) : null}
        </Panel>

        <Panel>
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-ink-3">Source</p>
          <div className="mt-4 flex items-start gap-3">
            <Box aria-hidden="true" className="mt-0.5 size-5 text-accent" />
            <div>
              <p className="text-sm font-semibold text-ink">
                {request.source.type === 'track' ? 'Single track' : 'Generation outputs'}
              </p>
              <p className="mt-1 break-all font-mono text-xs leading-5 text-ink-3">
                {request.source.type === 'track'
                  ? request.source.track_id
                  : request.source.generation_id}
              </p>
            </div>
          </div>
        </Panel>
      </div>

      <section className="mt-6">
        <h2 className="mb-3 text-lg font-semibold text-ink">Tracks</h2>
        {deliveries.length === 0 ? (
          <StateSurface title="No delivery tracks yet">
            The request may still be expanding its source into track deliveries.
          </StateSurface>
        ) : (
          <div className="grid gap-3 md:block md:overflow-hidden md:rounded-md md:border md:border-border">
            {deliveries.map(({ delivery, track }) => (
              <DeliveredTrack
                delivery={delivery}
                key={delivery.id}
                onStart={() => startDeliveryPlayback(track.id)}
                track={track}
              />
            ))}
          </div>
        )}
        {detail.hasNextPage ? (
          <div className="mt-5 flex justify-center">
            <Button
              disabled={detail.isFetchingNextPage}
              onClick={() => void detail.fetchNextPage()}
              variant="secondary"
            >
              {detail.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        ) : null}
      </section>
    </article>
  );
}
